import React from 'react'
import { pickAll } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import { Header } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { isProductFilter } from '../../lib/filters'
import {
	useDashboardStore,
	useSelectDashboardFilters,
	useSelectDashboardFiltersKeys,
} from '../../lib/store'
import AlertsWidget from '../widgets/Alerts/Widget'
import CompetitorCPIWidget from '../widgets/CompetitorCPI/Widget'
import NumericAverageCPIWidget from '../widgets/NumericAverageCPI/Widget'
import NumericAveragePriceChangeWidget from '../widgets/NumericAveragePriceChange/Widget'
import NumericGrossMarginImpactWidget from '../widgets/NumericGrossMarginImpact/Widget'
import NumericGrossProfitImpactWidget from '../widgets/NumericGrossProfitImpact/Widget'
import NumericItemsIntegratedWidget from '../widgets/NumericItemsIntegrated/Widget'
import NumericReadyForOptimizationWidget from '../widgets/NumericReadyForOptimization/Widget'
import NumericRevenueImpactWidget from '../widgets/NumericRevenueImpact/Widget'
import NumericSalesUnitsImpactWidget from '../widgets/NumericSelesUnitsImpact/Widget'
import NumericUnasignedItemsWidget from '../widgets/NumericUassignedItems/Widget'
import OptimizedPricesSegmentedWidget from '../widgets/OptimizedPricesSegmented/Widget'
import PricingDistributionSegmentedWidget from '../widgets/PricingDistributionSegmented/Widget'
import SubcategoryPerformanceWidget from '../widgets/SubcategoryPerformance/Widget'
import { useFilterFieldsConfig } from './useFilterFiledsConfig'

export default function MainDashboardOperationsSection() {
	const { t } = useTranslation()

	const { setFilters, filters } = useDashboardStore(
		useSelectDashboardFilters('main_operations')
	)
	const { storedKeys, setStoredKeys } = useDashboardStore(
		useSelectDashboardFiltersKeys('main_operations')
	)
	const { fieldsConfig, isLoading } = useFilterFieldsConfig({
		// remove fields that unsuported by BI widgets in this dashboard section
		sanitizeFields,
	})
	const onlyProductFilters = filters.filter(isProductFilter) || []

	return (
		<section>
			<Header className='sticky top-0 z-10 items-center justify-between bg-background/50 px-4 py-2 backdrop-blur-md'>
				<div className='flex items-center'>
					<h2 className='items-center text-lg font-bold'>
						{t('dashboard.section.operational.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<InfoIcon className='ml-2 size-4' />
						</TooltipTrigger>
						<TooltipContent>
							{t('dashboard.section.operational.tooltip')}
						</TooltipContent>
					</Tooltip>
				</div>
				{isLoading ? (
					<div className='flex items-center space-x-2'>
						<Skeleton className='h-6 w-28' />
						<Skeleton className='h-6 w-20' />
						<Skeleton className='h-6 w-24' />
					</div>
				) : (
					<QuickFiltersBar
						irremovableKeys={[
							'competitor_name',
							'optimization_group_id',
							'pos_ids',
							'brand',
							'category_ids',
						]}
						rules={filters}
						storedKeys={storedKeys}
						onSaveStoredKeys={(keys) => setStoredKeys('main_operations', keys)}
						onApply={(rules) => {
							setFilters('main_operations', rules.map(formatFilterRule))
						}}
						fieldsConfig={fieldsConfig}
						limit={7}
					/>
				)}
			</Header>
			<div className='grid min-h-0 grid-cols-6 gap-4 p-4'>
				<NumericReadyForOptimizationWidget commonFilters={onlyProductFilters} />
				<OptimizedPricesSegmentedWidget
					className='col-span-2'
					commonFilters={onlyProductFilters}
				/>
				<NumericRevenueImpactWidget commonFilters={onlyProductFilters} />
				<NumericSalesUnitsImpactWidget commonFilters={onlyProductFilters} />
				<NumericAveragePriceChangeWidget commonFilters={onlyProductFilters} />

				{/*  */}
				<NumericItemsIntegratedWidget commonFilters={onlyProductFilters} />
				<PricingDistributionSegmentedWidget
					className='col-span-2'
					commonFilters={onlyProductFilters}
				/>
				<NumericGrossProfitImpactWidget commonFilters={onlyProductFilters} />
				<NumericGrossMarginImpactWidget commonFilters={onlyProductFilters} />
				<NumericAverageCPIWidget commonFilters={filters || []} />
				<NumericUnasignedItemsWidget
					className='row-span-2'
					commonFilters={onlyProductFilters}
				/>
				<SubcategoryPerformanceWidget
					className='col-span-3 row-span-2'
					commonFilters={filters || []}
				/>
				<CompetitorCPIWidget
					className='col-span-2 row-span-2'
					commonFilters={filters || []}
				/>
				<AlertsWidget className='col-span-2 col-start-5 row-span-2 h-full min-h-0' />
			</div>
		</section>
	)
}

const sanitizeFields = pickAll([
	'competitor_name',
	'optimization_group_id',
	'pos_ids',
	'brand',
	'category_ids',
])
